import {
  getOrganizationsForManualContract,
  getPropertiesForManualContract,
  getSubscriptionsForManualContract,
  getTeamForManualContract,
  plansAll,
} from "types/gql-types";
import { toLocalIsoDate } from "utils/date";
import { combineAddress } from "utils/address";
import { ManualContractFormValues, Option, PropertyOption } from "./form";

const toInputDate = (value: Date | null): string | null =>
  (value instanceof Date && !Number.isNaN(value.getTime()) ? toLocalIsoDate(value) : null);

const blankToNull = (value: string) => value || null;

const toInputUnitCount = (value: ManualContractFormValues["unitCount"]): number | null => {
  if (typeof value === "number") return value;
  if (value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const buildPropertyOptions = (
  data?: getPropertiesForManualContract,
): PropertyOption[] =>
  (data?.properties ?? []).map((p) => ({
    value: p.id,
    label: p.name,
    units: p.units ?? null,
    phone: p.phone ?? "",
    email: p.email ?? "",
    address1: p.address?.address1 ?? "",
    address2: p.address?.address2 ?? "",
    city: p.address?.city ?? "",
    state: p.address?.state ?? "",
    zip: p.address?.zip ?? "",
    organizationId: p.organization?.id ?? "",
  }));

export const buildSignerOptions = (
  data?: getTeamForManualContract,
): Option[] =>
  (data?.organization?.team ?? []).map((user) => ({
    value: user.id,
    label: `${user.fullName || "No Name"} (${user.email})${user.isArchived ? " — Inactive" : ""}`,
  }));

export const buildPlanOptions = (data?: plansAll): Option[] =>
  (data?.plansAll ?? []).map((plan) => ({ value: plan.id, label: plan.name }));

export const buildOrganizationOptions = (
  data?: getOrganizationsForManualContract,
): Option[] =>
  (data?.organizations ?? []).map((org) => ({ value: org.id, label: org.name }));

// One label format for a subscription wherever the contract admin UI names one
// (typeahead options, linked-subscription summary).
export const subscriptionLabel = (sub: {
  startDate?: string | null;
  endDate?: string | null;
  plan?: { name?: string | null } | null;
}): string => `${sub.plan?.name ?? "Plan"} · ${sub.startDate ?? "?"} → ${sub.endDate ?? "?"}`;

export const buildSubscriptionOptions = (
  data?: getSubscriptionsForManualContract,
): Option[] =>
  (data?.subscriptionsForProperty ?? []).map((sub) => ({
    value: sub.id,
    label: subscriptionLabel(sub),
  }));

export const toMutationInput = (values: ManualContractFormValues) => {
  const postSign = Boolean(values.runPostSignSetup);

  return {
    // Toggle ON creates a new draft property server-side, so no property is selected.
    propertyId: postSign ? null : values.propertyId,
    organizationId: postSign ? values.organizationId || null : null,
    propertyName: postSign ? values.propertyName || null : null,
    runPostSignSetup: postSign,
    signedPdfBase64: values.signedPdfBase64 || null,
    subscriptionId: values.subscriptionId || null,
    planId: blankToNull(values.planId),
    unitCount: toInputUnitCount(values.unitCount),
    signedAt: toInputDate(values.signedAt),
    billingFrequency: blankToNull(values.billingFrequency),
    contractStartDate: toInputDate(values.contractStartDate),
    initialTermEndDate: toInputDate(values.initialTermEndDate),
    propertyLegalName: blankToNull(values.propertyLegalName),
    status: values.status,
    signerUserId: values.signerUserId || null,
    propertyPhone: values.propertyPhone || null,
    propertyEmail: values.propertyEmail || null,
    propertyAddress: combineAddress(values) || null,
    propertyAddressParts: postSign ? {
      address1: values.address1 || null,
      address2: values.address2 || null,
      city: values.city || null,
      state: values.state || null,
      zip: values.zip || null,
    } : null,
  };
};
